import { vec2, vec3 } from "gl-matrix"
import { lms_to_xyz, srgb_to_xyz, xyz_to_lms } from "@/lib/ai/calibration/ai-calibration-utils"
import { XYZtoXY, XYtoXYZ } from "./color-conversion"
import { illuminants } from "./color"

interface RUVT {
	r: number
	u: number
	v: number
	t: number
}

// Taken from DNG spec

const tintScale = -3000

// Table from Wyszecki & Stiles, "Color Science", second edition, page 228.

const tempTable: RUVT[] = [
	{ r: 0, u: 0.18006, v: 0.26352, t: -0.24341 },
	{ r: 10, u: 0.18066, v: 0.26589, t: -0.25479 },
	{ r: 20, u: 0.18133, v: 0.26846, t: -0.26876 },
	{ r: 30, u: 0.18208, v: 0.27119, t: -0.28539 },
	{ r: 40, u: 0.18293, v: 0.27407, t: -0.3047 },
	{ r: 50, u: 0.18388, v: 0.27709, t: -0.32675 },
	{ r: 60, u: 0.18494, v: 0.28021, t: -0.35156 },
	{ r: 70, u: 0.18611, v: 0.28342, t: -0.37915 },
	{ r: 80, u: 0.1874, v: 0.28668, t: -0.40955 },
	{ r: 90, u: 0.1888, v: 0.28997, t: -0.44278 },
	{ r: 100, u: 0.19032, v: 0.29326, t: -0.47888 },
	{ r: 125, u: 0.19462, v: 0.30141, t: -0.58204 },
	{ r: 150, u: 0.19962, v: 0.30921, t: -0.70471 },
	{ r: 175, u: 0.20525, v: 0.31647, t: -0.84901 },
	{ r: 200, u: 0.21142, v: 0.32312, t: -1.0182 },
	{ r: 225, u: 0.21807, v: 0.32909, t: -1.2168 },
	{ r: 250, u: 0.22511, v: 0.33439, t: -1.4512 },
	{ r: 275, u: 0.23247, v: 0.33904, t: -1.7298 },
	{ r: 300, u: 0.2401, v: 0.34308, t: -2.0637 },
	{ r: 325, u: 0.24702, v: 0.34655, t: -2.4681 },
	{ r: 350, u: 0.25591, v: 0.34951, t: -2.9641 },
	{ r: 375, u: 0.264, v: 0.352, t: -3.5814 },
	{ r: 400, u: 0.27218, v: 0.35407, t: -4.3633 },
	{ r: 425, u: 0.28039, v: 0.35577, t: -5.3762 },
	{ r: 450, u: 0.28863, v: 0.35714, t: -6.7262 },
	{ r: 475, u: 0.29685, v: 0.35823, t: -8.5955 },
	{ r: 500, u: 0.30505, v: 0.35907, t: -11.324 },
	{ r: 525, u: 0.3132, v: 0.35968, t: -15.628 },
	{ r: 550, u: 0.32129, v: 0.36011, t: -23.325 },
	{ r: 575, u: 0.32931, v: 0.36038, t: -40.77 },
	{ r: 600, u: 0.33724, v: 0.36051, t: -116.45 },
]

export function XYtoWhiteBalance(xy: vec2): vec2 {
	let temperature = 0
	let tint = 0

	// Convert to uv space.

	const u = (2.0 * xy[0]) / (1.5 - xy[0] + 6.0 * xy[1])
	const v = (3.0 * xy[1]) / (1.5 - xy[0] + 6.0 * xy[1])

	// Search for line pair coordinate is between.

	let last_dt = 0.0
	let last_dv = 0.0
	let last_du = 0.0

	for (let index = 1; index <= 30; index++) {
		// Convert slope to delta-u and delta-v, with length 1.

		const tempTableAtIndex = tempTable[index] as RUVT
		const tempTableAtPriorIndex = tempTable[index - 1] as RUVT

		let du = 1.0
		let dv = tempTableAtIndex.t

		let len = Math.sqrt(1.0 + dv * dv)

		du /= len
		dv /= len

		// Find delta from black body point to test coordinate.

		let uu = u - tempTableAtIndex.u
		let vv = v - tempTableAtIndex.v

		// Find distance above or below line.

		let dt = -uu * dv + vv * du

		// If below line, we have found line pair.

		if (dt <= 0.0 || index == 30) {
			// Find fractional weight of two lines.

			if (dt > 0.0) dt = 0.0

			dt = -dt

			let f

			if (index == 1) {
				f = 0.0
			} else {
				f = dt / (last_dt + dt)
			}

			// Interpolate the temperature.

			temperature = 1.0e6 / (tempTableAtPriorIndex.r * f + tempTableAtIndex.r * (1.0 - f))

			// Find delta from black body point to test coordinate.

			uu = u - (tempTableAtPriorIndex.u * f + tempTableAtIndex.u * (1.0 - f))
			vv = v - (tempTableAtPriorIndex.v * f + tempTableAtIndex.v * (1.0 - f))

			// Interpolate vectors along slope.

			du = du * (1.0 - f) + last_du * f
			dv = dv * (1.0 - f) + last_dv * f

			len = Math.sqrt(du * du + dv * dv)

			du /= len
			dv /= len

			// Find distance along slope.

			tint = (uu * du + vv * dv) * tintScale

			break
		}

		// Try next line pair.

		last_dt = dt
		last_du = du
		last_dv = dv
	}

	return [temperature, tint]
}

export function whiteBalanceToXY([temperature, tint]: vec2): vec2 {
	let x = 0
	let y = 0

	// Find inverse temperature to use as index.

	const r = 1.0e6 / temperature

	// Convert tint to offset is uv space.

	const offset = tint * (1.0 / tintScale)

	// Search for line pair containing coordinate.

	for (let index = 0; index <= 29; index++) {
		const plus1R = tempTable[index + 1]?.r as number
		if (r < plus1R || index == 29) {
			// Find relative weight of first line.

			const f = (plus1R - r) / (plus1R - (tempTable[index]?.r as number))

			// Interpolate the black body coordinates.

			let u =
				(tempTable[index]?.u as number) * f +
				(tempTable[index + 1]?.u as number) * (1.0 - f)
			let v =
				(tempTable[index]?.v as number) * f +
				(tempTable[index + 1]?.v as number) * (1.0 - f)

			// Find vectors along slope for each line.

			let uu1 = 1.0
			let vv1 = tempTable[index]?.t as number

			let uu2 = 1.0
			let vv2 = tempTable[index + 1]?.t as number

			const len1 = Math.sqrt(1.0 + vv1 * vv1)
			const len2 = Math.sqrt(1.0 + vv2 * vv2)

			uu1 /= len1
			vv1 /= len1

			uu2 /= len2
			vv2 /= len2

			// Find vector from black body point.

			let uu3 = uu1 * f + uu2 * (1.0 - f)
			let vv3 = vv1 * f + vv2 * (1.0 - f)

			const len3 = Math.sqrt(uu3 * uu3 + vv3 * vv3)

			uu3 /= len3
			vv3 /= len3

			// Adjust coordinate along this vector.

			u += uu3 * offset
			v += vv3 * offset

			// Convert to xy coordinates.

			x = (1.5 * u) / (u - 4.0 * v + 2.0)
			y = v / (u - 4.0 * v + 2.0)

			break
		}
	}

	return [x, y]
}

/**
 * Given an illuminant color (e.g. a user's color-pick on a neutral surface),
 * compute the temperature/tint values that neutralize it.
 *
 * This is chromatic adaptation: we want to shift the white balance so that
 * the picked color maps to neutral white. We work in LMS color space (models
 * human cone responses — Long, Medium, Short) because per-channel scaling in
 * LMS is a good approximation of how our vision adapts to illuminants.
 *
 * Derivation (all in LMS space):
 *
 *   1. Standard chromatic adaptation (Von Kries):
 *      gain = lms_D65 / lms_estimated
 *      where lms_estimated is the picked illuminant, and D65 is the standard
 *      daylight illuminant used by displays as the neutral target.
 *
 *   2. Our shader applies temp/tint as:
 *      gain' = lms_asshot / lms_adjusted
 *      where lms_asshot is the camera's original white balance, and
 *      lms_adjusted is derived from the temp/tint slider values.
 *
 *   3. Equating the two gains:
 *      lms_adjusted = lms_asshot * lms_estimated / lms_D65
 *
 *   4. Convert lms_adjusted back to a temperature/tint pair.
 *
 * Reference for chromatic adaptation background (step 1):
 * https://santhalakshminarayana.github.io/blog/chromatic-adaptation
 * Steps 2–4 are specific to our shader's temp/tint implementation.
 */
export function RGBToWhiteBalance(
	asShotTemperature: number,
	asShotTint: number,
	r: number,
	g: number,
	b: number,
): [number, number] {
	const xyz_src = srgb_to_xyz([r / 255, g / 255, b / 255])

	const lms_estimated = xyz_to_lms(xyz_src)
	const lms_D65 = xyz_to_lms(illuminants.D65)

	const xyz_as_shot = XYtoXYZ(whiteBalanceToXY([asShotTemperature, asShotTint]))

	const lms_as_shot = xyz_to_lms(xyz_as_shot)

	const lms_adjusted: vec3 = [
		(lms_as_shot[0] * lms_estimated[0]) / lms_D65[0],
		(lms_as_shot[1] * lms_estimated[1]) / lms_D65[1],
		(lms_as_shot[2] * lms_estimated[2]) / lms_D65[2],
	]

	const xyz = lms_to_xyz(lms_adjusted)

	return XYtoWhiteBalance(XYZtoXY(xyz))
}
